import { SourcererOptions, flags } from "@movie-web/providers";

const vidSrcEmbedBases = [
  "https://vidsrc-embed.ru",
  "https://vidsrc-embed.su",
  "https://vidsrcme.su",
  "https://vsrc.su",
];

const playlistTokenSources = {
  __TOKEN__: "https://gestaltgazetteer.website/generate.php",
  __TOKENPG__: "https://app2.putgate.com/generate.php",
} as const;

type MovieVidSrcContext = Parameters<
  NonNullable<SourcererOptions["scrapeMovie"]>
>[0];
type ShowVidSrcContext = Parameters<
  NonNullable<SourcererOptions["scrapeShow"]>
>[0];
type VidSrcContext = MovieVidSrcContext | ShowVidSrcContext;
type Sourcerer = SourcererOptions & {
  type: "source";
  disabled: boolean;
  mediaTypes: Array<"movie" | "show">;
};

function toAbsoluteUrl(url: string, base: string) {
  if (url.startsWith("//")) return `https:${url}`;
  return new URL(url, base).toString();
}

function getPlayerIframeUrl(html: string, baseUrl: string) {
  const src = html.match(
    /<iframe[^>]+id=["']player_iframe["'][^>]+src=["']([^"']+)/,
  )?.[1];
  if (!src) throw new Error("No VidSrc player iframe found");
  return toAbsoluteUrl(src, baseUrl);
}

function getProRcpUrl(html: string, baseUrl: string) {
  const src = html.match(/src:\s*["']([^"']*\/prorcp\/[^"']+)["']/)?.[1];
  if (!src) throw new Error("No VidSrc prorcp URL found");
  return toAbsoluteUrl(src, baseUrl);
}

function getMasterUrls(html: string) {
  const urls = html.match(/var\s+master_urls\s*=\s*"([^"]+)"/)?.[1];
  if (!urls) throw new Error("No VidSrc master playlist found");
  return urls;
}

async function fetchText(ctx: VidSrcContext, url: string, referer?: string) {
  return ctx.fetcher<string>(url, {
    headers: referer ? { referer } : undefined,
  });
}

async function applyPlaylistTokens(ctx: VidSrcContext, masterUrls: string) {
  let output = masterUrls;
  await Promise.all(
    Object.entries(playlistTokenSources).map(async ([placeholder, url]) => {
      if (!output.includes(placeholder)) return;
      const token = await fetchText(ctx, url);
      output = output.replaceAll(placeholder, token.trim());
    }),
  );
  return output;
}

async function selectPlaylist(ctx: VidSrcContext, masterUrls: string) {
  const candidates = masterUrls
    .split(/\s+or\s+/)
    .map((url) => url.trim())
    .filter((url) => url.length > 0);

  if (candidates.length === 0) throw new Error("No VidSrc playlist candidates");

  for (const candidate of candidates) {
    try {
      const response = await ctx.fetcher.full(candidate, { method: "HEAD" });
      if (response.statusCode >= 200 && response.statusCode < 400)
        return candidate;
    } catch {
      // Try the next mirror before surfacing a source failure.
    }
  }

  return candidates[0];
}

async function scrapeVidSrc(ctx: VidSrcContext) {
  const mediaId = ctx.media.imdbId ?? ctx.media.tmdbId;
  const path =
    ctx.media.type === "movie"
      ? `/embed/movie/${mediaId}`
      : `/embed/tv/${mediaId}/${ctx.media.season.number}-${ctx.media.episode.number}`;

  let lastError: unknown;

  for (const base of vidSrcEmbedBases) {
    try {
      const embedUrl = `${base}${path}`;
      const embedHtml = await fetchText(ctx, embedUrl);
      const iframeUrl = getPlayerIframeUrl(embedHtml, embedUrl);
      const rcpHtml = await fetchText(ctx, iframeUrl, embedUrl);
      const prorcpUrl = getProRcpUrl(rcpHtml, iframeUrl);
      const prorcpHtml = await fetchText(ctx, prorcpUrl, iframeUrl);
      const masterUrls = await applyPlaylistTokens(
        ctx,
        getMasterUrls(prorcpHtml),
      );
      const playlist = await selectPlaylist(ctx, masterUrls);

      return {
        stream: [
          {
            id: "primary",
            type: "hls" as const,
            playlist,
            captions: [],
            flags: [flags.CORS_ALLOWED],
            preferredHeaders: {
              Referer: prorcpUrl,
              Origin: new URL(prorcpUrl).origin,
            },
          },
        ],
        embeds: [],
      };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError instanceof Error ? lastError : new Error("VidSrc failed");
}

export const vidSrcSource: Sourcerer = {
  type: "source",
  id: "vidsrc",
  name: "VidSrc",
  rank: 210,
  disabled: false,
  mediaTypes: ["movie", "show"],
  flags: [flags.CORS_ALLOWED],
  scrapeMovie: (ctx) => scrapeVidSrc(ctx),
  scrapeShow: (ctx) => scrapeVidSrc(ctx),
};
